/**
 * View Context (DRR-288)
 *
 * Unified view context for frontend rendering.
 * Single source of truth for all presentation metadata:
 * - Lens (clinical, operational, architect)
 * - Terminology mappings
 * - Visible primitives
 * - Navigation groups
 * - Form overrides
 * - Scope dimensions
 * - Work context
 * - Route constraints
 *
 * Frontend calls myViewContext(route) once per navigation and caches.
 * Invalidation happens via response extensions on state changes.
 */

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  type ReactNode,
} from 'react';
import { useLocation } from 'react-router-dom';
import { useMyViewContextQuery } from '@/generated/graphql';

// View lens enum (matches backend GqlViewLens)
export type ViewLens = 'CLINICAL' | 'OPERATIONAL' | 'ARCHITECT' | 'FAMILY' | 'USER';

// Nav group reference
export interface NavGroupRef {
  id: string;
  label: string;
  icon: string | null;
  routes: string[];
  order: number;
}

// Form override reference
export interface FormOverrideRef {
  id: string;
  baseSchemaId: string;
  overrideType: string;
}

// Scope dimension
export interface ScopeDimension {
  id: string;
  label: string;
  renderer: string;
  active: boolean;
}

// Work context (subject-centric routes)
export interface WorkContext {
  constellationId: string | null;
  phase: string | null;
  subjectType: string | null;
  subjectName: string | null;
  enrichmentKeys: string[];
}

// Route constraints (informational)
export interface RouteConstraints {
  dataTier: 'T1' | 'T2' | 'T3' | 'T4' | null;
  safetyLevel: 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW' | null;
  requiredScopes: string[];
  authTier: number | null;
}

// Full view context
export interface ViewContextState {
  lens: ViewLens;
  terminology: Record<string, string>;
  visiblePrimitives: string[];
  navGroups: NavGroupRef[];
  formOverrides: FormOverrideRef[];
  scopeDimensions: ScopeDimension[];
  workContext: WorkContext | null;
  constraints: RouteConstraints;
  loading: boolean;
  error: Error | null;
  refetch: () => void;
}

// Default state
const defaultState: ViewContextState = {
  lens: 'USER',
  terminology: {},
  visiblePrimitives: [],
  navGroups: [],
  formOverrides: [],
  scopeDimensions: [],
  workContext: null,
  constraints: {
    dataTier: null,
    safetyLevel: null,
    requiredScopes: [],
    authTier: null,
  },
  loading: true,
  error: null,
  refetch: () => {},
};

const ViewContext = createContext<ViewContextState>(defaultState);

/**
 * View Context Provider
 *
 * Wraps the app and provides view context based on current route.
 * Fetches context on route change and caches result.
 */
export function ViewContextProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const [route, setRoute] = useState(location.pathname);

  // Update route on navigation
  useEffect(() => {
    setRoute(location.pathname);
  }, [location.pathname]);

  // Fetch view context
  const { data, loading, error, refetch } = useMyViewContextQuery({
    variables: { route },
    fetchPolicy: 'cache-first',
    nextFetchPolicy: 'cache-first',
  });

  // Transform data to state
  const state = useMemo<ViewContextState>(() => {
    if (!data?.myViewContext) {
      return { ...defaultState, loading, error: error ?? null, refetch };
    }

    const ctx = data.myViewContext;
    return {
      lens: ctx.lens as ViewLens,
      terminology: ctx.terminology as Record<string, string>,
      visiblePrimitives: ctx.visiblePrimitives,
      navGroups: ctx.navGroups.map((ng) => ({
        id: ng.id,
        label: ng.label,
        icon: ng.icon ?? null,
        routes: ng.routes,
        order: ng.order,
      })),
      formOverrides: ctx.formOverrides.map((fo) => ({
        id: fo.id,
        baseSchemaId: fo.baseSchemaId,
        overrideType: fo.overrideType,
      })),
      scopeDimensions: ctx.scopeDimensions.map((sd) => ({
        id: sd.id,
        label: sd.label,
        renderer: sd.renderer,
        active: sd.active,
      })),
      workContext: ctx.workContext
        ? {
            constellationId: ctx.workContext.constellationId ?? null,
            phase: ctx.workContext.phase ?? null,
            subjectType: ctx.workContext.subjectType ?? null,
            subjectName: ctx.workContext.subjectName ?? null,
            enrichmentKeys: ctx.workContext.enrichmentKeys,
          }
        : null,
      constraints: {
        dataTier: ctx.constraints.dataTier as RouteConstraints['dataTier'],
        safetyLevel: ctx.constraints.safetyLevel as RouteConstraints['safetyLevel'],
        requiredScopes: ctx.constraints.requiredScopes,
        authTier: ctx.constraints.authTier ?? null,
      },
      loading,
      error: error ?? null,
      refetch,
    };
  }, [data, loading, error, refetch]);

  return <ViewContext.Provider value={state}>{children}</ViewContext.Provider>;
}

/**
 * useViewContext hook
 *
 * Returns the current view context for rendering decisions.
 * Call once per component that needs presentation metadata.
 *
 * @example
 * const { lens, terminology, navGroups } = useViewContext();
 * const label = terminology['Patient'] ?? 'Patient'; // "Resident" for ALF
 */
export function useViewContext(): ViewContextState {
  return useContext(ViewContext);
}

/**
 * useTerminology hook
 *
 * Returns terminology resolver function.
 * Shorthand for common terminology lookups.
 *
 * @example
 * const t = useTerminology();
 * return <span>{t('Patient')}</span>; // "Resident" for ALF
 */
export function useTerminology(): (concept: string, fallback?: string) => string {
  const { terminology } = useViewContext();
  return useCallback(
    (concept: string, fallback?: string) => terminology[concept] ?? fallback ?? concept,
    [terminology]
  );
}

/**
 * useVisiblePrimitives hook
 *
 * Returns array of visible primitive types for current role.
 * Use to conditionally render primitive-specific UI.
 *
 * @example
 * const visible = useVisiblePrimitives();
 * if (visible.includes('Workflow')) { ... }
 */
export function useVisiblePrimitives(): string[] {
  const { visiblePrimitives } = useViewContext();
  return visiblePrimitives;
}

/**
 * useNavGroups hook
 *
 * Returns navigation groups for current role.
 * Use to render role-appropriate navigation.
 */
export function useNavGroups(): NavGroupRef[] {
  const { navGroups } = useViewContext();
  return navGroups;
}

/**
 * useWorkContext hook
 *
 * Returns work context for subject-centric routes.
 * Null if route is not subject-centric.
 */
export function useWorkContext(): WorkContext | null {
  const { workContext } = useViewContext();
  return workContext;
}

/**
 * useRouteConstraints hook
 *
 * Returns route constraints (informational only).
 * Backend RequireScope is the authority; this is for UI hints.
 */
export function useRouteConstraints(): RouteConstraints {
  const { constraints } = useViewContext();
  return constraints;
}
